import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Add as AddIcon, Edit as EditIcon, ExitToApp as ExitIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';

import { AlertBadge, type AlertType as BadgeType } from '@/components/data/AlertBadge';
import { DataDisplay } from '@/components/data/DataDisplay';
import {
  type DetailTab,
  ResidentDetailDrawer,
} from '@/components/residents/detail/ResidentDetailDrawer';
import { DischargeConfirmDialog } from '@/components/residents/DischargeConfirmDialog';
import {
  type ResidentFormInitial,
  ResidentFormDialog,
} from '@/components/residents/ResidentFormDialog';
import { UsageSummaryBar } from '@/components/residents/UsageSummaryBar';
import { VOICE_SCORE_LABELS } from '@/lib/api/voice-history';
import { useHasRole } from '@/lib/hooks/useHasRole';
import {
  type ResidentAlert,
  type ResidentListItem,
  type ResidentListSortKey,
  type ResidentStatus,
  listResidentsForDashboard,
} from '@/lib/api/residents';

const STATUS_LABEL: Record<ResidentStatus, string> = {
  ADMITTED: '입소중',
  DISCHARGED: '퇴소',
};

const TYPE_LABEL: Record<string, string> = {
  RESIDENT: '입소',
  OUTPATIENT_A: '외래 A',
  OUTPATIENT_B: '외래 B',
};

const ALERT_TYPE_MAP: Record<ResidentAlert['type'], BadgeType> = {
  UNUSED: 'unused',
  SCORE_DROP: 'drop',
  VOICE_UNUSED: 'voice-unused',
};

const SORT_MAP: Record<string, ResidentListSortKey> = {
  room: 'room',
  'bgtScore.avgScore_asc': 'bgtScoreAsc',
  'voiceWellnessScore.hearingScore_asc': 'voiceWellnessAsc',
};

export function ResidentsPage() {
  const canCreate = useHasRole('ADMIN', 'MANAGER');
  const canDischarge = useHasRole('ADMIN', 'MANAGER');

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 30,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'room', sort: 'asc' }]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<'' | ResidentStatus>('ADMITTED');
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');

  // P3-2 폴백 호환: ?q=환자번호 진입 시 검색 채우고 정리
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q !== search) setSearch(q);
    // filter는 초기 상태로만 소비했으므로 URL에서 정리한다 — 남겨두면 체크박스를
    // 해제해도 URL이 필터를 가리켜 새로고침 시 되살아난다.
    if (q || searchParams.get('filter')) {
      const next = new URLSearchParams(searchParams);
      next.delete('q');
      next.delete('filter');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // 상세 패널 URL state — ?detail=<residentId>&tab=today|period|voice|history
  const detailId = searchParams.get('detail');
  const detailTab = (searchParams.get('tab') as DetailTab) ?? 'today';

  const openDetail = (id: string, tab: DetailTab = 'today') => {
    const next = new URLSearchParams(searchParams);
    next.set('detail', id);
    next.set('tab', tab);
    setSearchParams(next);
  };

  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('detail');
    next.delete('tab');
    setSearchParams(next);
  };

  const changeDetailTab = (tab: DetailTab) => {
    if (!detailId) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  // 대시보드 카드에서 ?filter=unused|dropped|alert 로 진입 (초기값만 사용).
  // 이후 체크박스 조작은 로컬 상태로만 다뤄 URL과 화면이 어긋나지 않게 한다.
  const initialFilter = searchParams.get('filter');
  const [alertOnly, setAlertOnly] = useState(initialFilter === 'alert');
  const [unusedOnly, setUnusedOnly] = useState(initialFilter === 'unused');
  const [droppedOnly, setDroppedOnly] = useState(initialFilter === 'dropped');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ResidentFormInitial | null>(null);
  const [dischargeTarget, setDischargeTarget] = useState<ResidentListItem | null>(null);

  const orderBy = useMemo<ResidentListSortKey>(() => {
    const first = sortModel[0];
    if (!first) return 'room';
    const key = `${first.field}_${first.sort}` as keyof typeof SORT_MAP;
    return SORT_MAP[key] ?? SORT_MAP[first.field] ?? 'room';
  }, [sortModel]);

  const query = useQuery({
    queryKey: [
      'residents',
      'dashboard',
      pagination.page,
      pagination.pageSize,
      status,
      alertOnly,
      unusedOnly,
      droppedOnly,
      orderBy,
    ],
    queryFn: () =>
      listResidentsForDashboard({
        page: pagination.page + 1,
        limit: pagination.pageSize,
        status: status || undefined,
        alert: alertOnly || undefined,
        unused: unusedOnly || undefined,
        dropped: droppedOnly || undefined,
        orderBy,
      }),
    placeholderData: (prev) => prev,
  });

  const filteredRows = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      const name = r.user?.name?.toLowerCase() ?? '';
      const roomNumber = r.room?.roomNumber ?? '';
      const patientCode = r.user?.patientCode?.toLowerCase() ?? '';
      const birthDate = r.user?.birthDate ?? '';
      return (
        name.includes(q) ||
        roomNumber.includes(q) ||
        patientCode.includes(q) ||
        birthDate.includes(q)
      );
    });
  }, [query.data?.items, search]);

  const columns: GridColDef<ResidentListItem>[] = useMemo(
    () => [
      {
        field: 'room',
        headerName: '호실',
        width: 90,
        valueGetter: (_, row) => row.room?.roomNumber ?? '—',
      },
      {
        field: 'floor',
        headerName: '층',
        width: 60,
        sortable: false,
        valueGetter: (_, row) => (row.room ? `${row.room.floor}F` : '—'),
      },
      {
        field: 'name',
        headerName: '이름',
        flex: 1,
        minWidth: 170,
        sortable: false,
        valueGetter: (_, row) => row.user?.name ?? '(미지정)',
        renderCell: (params) =>
          params.row.user?.name ? (
            <Typography variant="body2">{params.row.user.name}</Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              {params.row.userId == null ? '회원 미연결' : '(미지정)'}
            </Typography>
          ),
      },
      {
        field: 'patientCode',
        headerName: '환자번호',
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.row.user?.patientCode ? (
            <Typography variant="body2">{params.row.user.patientCode}</Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: 'type',
        headerName: '구분',
        width: 90,
        sortable: false,
        valueGetter: (_, row) => TYPE_LABEL[row.type] ?? row.type,
      },
      {
        field: 'status',
        headerName: '상태',
        width: 90,
        sortable: false,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STATUS_LABEL[params.row.status]}
            color={params.row.status === 'ADMITTED' ? 'success' : 'default'}
            variant={params.row.status === 'ADMITTED' ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'bgtScore.avgScore',
        headerName: 'BGT 평균점수',
        width: 140,
        renderCell: (params) => (
          <DataDisplay
            value={params.row.bgtScore?.avgScore}
            previousValue={params.row.yesterdayBgtScore?.avgScore}
            suffix="점"
          />
        ),
      },
      //
      // 세 검사는 합산하지 않는 독립 지표라 컬럼도 각각 나눈다. 한 칸에 묶어
      // '68 / 71 / 55'로 두면 어느 숫자가 무엇인지 헤더만 보고 알 수 없다.
      // 계정 연결 변경은 목록이 아니라 상세 패널(LinkedAccounts)에서 한다.
      {
        field: 'voiceWellnessScore.hearingScore',
        headerName: VOICE_SCORE_LABELS.hearingScore,
        width: 110,
        sortable: true,
        renderCell: (params) => {
          const v = params.row.voiceWellnessScore;
          return params.row.voiceUserId == null ? (
            <Typography variant="caption" color="text.disabled">
              계정 미연결
            </Typography>
          ) : (
            <DataDisplay value={v?.hasData ? v.hearingScore : null} suffix="점" />
          );
        },
      },
      {
        field: 'voiceWellnessScore.cognitiveScore',
        headerName: VOICE_SCORE_LABELS.cognitiveScore,
        width: 95,
        // 정렬은 백엔드가 소리찾기 기준만 지원한다(voiceWellnessAsc).
        // 여기에 정렬을 열어두면 눌러도 순서가 안 바뀌어 오해를 준다.
        sortable: false,
        renderCell: (params) => {
          const v = params.row.voiceWellnessScore;
          return <DataDisplay value={v?.hasData ? v.cognitiveScore : null} suffix="점" />;
        },
      },
      {
        field: 'voiceWellnessScore.stressScore',
        headerName: VOICE_SCORE_LABELS.stressScore,
        width: 95,
        sortable: false,
        renderCell: (params) => {
          const v = params.row.voiceWellnessScore;
          return <DataDisplay value={v?.hasData ? v.stressScore : null} suffix="점" />;
        },
      },
      {
        field: 'usedToday',
        headerName: '오늘 사용',
        width: 90,
        sortable: false,
        renderCell: (params) =>
          params.row.usedToday ? (
            <Chip size="small" label="사용" color="primary" variant="outlined" />
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: 'alerts',
        headerName: '경보',
        width: 230,
        sortable: false,
        renderCell: (params) =>
          params.row.alerts.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ) : (
            // 점+텍스트는 가로로 붙이면 '● 미사용 16일 ● 급락 -12%'로 읽혀 경계가 흐리다.
            <Stack spacing={0.25}>
              {params.row.alerts.map((a) => (
                <AlertBadge
                  key={a.type}
                  type={ALERT_TYPE_MAP[a.type]}
                  severity={a.severity}
                  detail={a.detail}
                />
              ))}
            </Stack>
          ),
      },
      {
        field: 'birthDate',
        headerName: '생년월일',
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.row.user?.birthDate ? (
            <Typography variant="body2">{params.row.user.birthDate}</Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: 'age',
        headerName: '나이',
        width: 70,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) =>
          params.row.user?.age !== null && params.row.user?.age !== undefined ? (
            <Typography variant="body2">{params.row.user.age}</Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          ),
      },
      {
        field: 'actions',
        headerName: '작업',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            {canCreate && (
              <Tooltip title="수정">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditTarget({
                      id: params.row.id,
                      userId: params.row.userId,
                      roomId: params.row.roomId,
                      voiceUserId: params.row.voiceUserId,
                      type: params.row.type,
                    });
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDischarge && params.row.status === 'ADMITTED' && (
              <Tooltip title="퇴소 처리">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDischargeTarget(params.row);
                  }}
                >
                  <ExitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canCreate, canDischarge],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">입소자 관리</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            신규 등록
          </Button>
        )}
      </Stack>

      <UsageSummaryBar />

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="이름·호실·환자번호·생년월일로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ color: '#9CA3AF' }}>🔍</span>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ minWidth: 240, flex: 1 }}
            />

            <TextField
              select
              size="small"
              label="상태"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as '' | ResidentStatus);
                setPagination((p) => ({ ...p, page: 0 }));
              }}
              sx={{ minWidth: 140 }}
              slotProps={{
                select: { displayEmpty: true },
                inputLabel: { shrink: true },
              }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="ADMITTED">입소중</MenuItem>
              <MenuItem value="DISCHARGED">퇴소</MenuItem>
            </TextField>

            <Stack direction="row" spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={alertOnly}
                    onChange={(e) => {
                      setAlertOnly(e.target.checked);
                      setPagination((p) => ({ ...p, page: 0 }));
                    }}
                  />
                }
                label="경보만"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={unusedOnly}
                    onChange={(e) => {
                      setUnusedOnly(e.target.checked);
                      setPagination((p) => ({ ...p, page: 0 }));
                    }}
                  />
                }
                label="미사용만"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={droppedOnly}
                    onChange={(e) => {
                      setDroppedOnly(e.target.checked);
                      setPagination((p) => ({ ...p, page: 0 }));
                    }}
                  />
                }
                label="급락만"
              />
            </Stack>
          </Stack>

          <Box sx={{ height: 'calc(100vh - 280px)', minHeight: 400 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              rowCount={query.data?.total ?? 0}
              loading={query.isLoading || query.isFetching}
              paginationMode="server"
              sortingMode="server"
              paginationModel={pagination}
              onPaginationModelChange={setPagination}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={[10, 20, 30, 50]}
              disableRowSelectionOnClick
              disableColumnMenu
              rowHeight={64}
              onRowClick={(params) => openDetail(params.row.id)}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <ResidentFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ResidentFormDialog
        open={!!editTarget}
        initial={editTarget ?? undefined}
        onClose={() => setEditTarget(null)}
      />
      <DischargeConfirmDialog resident={dischargeTarget} onClose={() => setDischargeTarget(null)} />
      <ResidentDetailDrawer
        residentId={detailId}
        tab={detailTab}
        onClose={closeDetail}
        onTabChange={changeDetailTab}
      />
    </Stack>
  );
}
